// Growth › Inquiries: questions from scorelm.com and league sites, newest
// first. Search and filters live in the URL (?q=&status=&topic=&page=).

import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { INQUIRY_STATUSES, type Inquiry, type InquiryStatus } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterChips,
  Pagination,
  RelativeTime,
  SearchInput,
  SelectInput,
  SkeletonRows,
  type Column,
} from '@/components/ui'
import { INQUIRY_STATUS_LABELS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { InquiryStatusBadge } from './shared'

const PAGE_SIZE = 25

function readParams(p: URLSearchParams) {
  const status = p.get('status')
  const page = Number(p.get('page'))
  return {
    q: p.get('q') ?? '',
    status: (INQUIRY_STATUSES as readonly string[]).includes(status ?? '') ? (status as InquiryStatus) : undefined,
    topic: p.get('topic') || undefined,
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

function Sender({ inquiry }: { inquiry: Inquiry }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="break-words font-semibold">{inquiry.name ?? <span className="font-medium text-muted">No name</span>}</span>
      <span className="font-mono text-xs font-medium text-muted [overflow-wrap:anywhere]">{inquiry.email}</span>
    </span>
  )
}

const COLUMNS: Column<Inquiry>[] = [
  { id: 'sender', header: 'From', primary: true, cell: (i) => <Sender inquiry={i} />, className: 'min-w-[13rem] max-w-[18rem]' },
  { id: 'organisation', header: 'Organisation', cell: (i) => <span className="text-muted">{i.organisation ?? '—'}</span> },
  { id: 'topic', header: 'Topic', cell: (i) => <span className="text-muted">{i.topic}</span> },
  { id: 'status', header: 'Status', cell: (i) => <InquiryStatusBadge status={i.status} /> },
  { id: 'received', header: 'Received', cell: (i) => <RelativeTime value={i.created_at} className="text-muted" /> },
]

export default function InquiriesListPage() {
  useDocumentTitle('Inquiries')
  const [searchParams, setSearchParams] = useSearchParams()
  const { q, status, topic, page } = readParams(searchParams)

  const update = (patch: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const params = useMemo(() => ({ q, status, topic, page, page_size: PAGE_SIZE }), [q, status, topic, page])
  const { data, error, status: loadStatus, isFetching, refetch } = useQuery(['growth', 'inquiries', params], (signal) => api.growth.inquiries.list(params, signal), { keepPrevious: true })

  const counts = data?.status_counts
  const rows = data?.inquiries ?? []
  const hasFilters = !!q || !!status || !!topic

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Inquiries" description="Questions from scorelm.com and league sites." />
      <div className="grid gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SearchInput label="Search inquiries" placeholder="Search name, email, organisation or message" value={q} onChange={(v) => update({ q: v })} className="sm:max-w-sm sm:flex-1" />
          <SelectInput
            aria-label="Filter by topic"
            value={topic ?? ''}
            options={[{ value: '', label: 'Any topic' }, ...(data?.topics ?? []).map((t) => ({ value: t, label: t }))]}
            onChange={(e) => update({ topic: e.target.value })}
            className="h-8 sm:ml-auto sm:w-44"
          />
        </div>
        <FilterChips
          label="Filter by status"
          options={(['all', ...INQUIRY_STATUSES] as const).map((s) => ({ value: s, label: s === 'all' ? 'All' : INQUIRY_STATUS_LABELS[s], count: counts ? counts[s] : undefined }))}
          value={status ?? 'all'}
          onChange={(v) => update({ status: v === 'all' ? undefined : v })}
        />
      </div>

      <Card flush className="overflow-hidden">
        {loadStatus === 'loading' ? (
          <SkeletonRows rows={6} label="Loading inquiries" />
        ) : loadStatus === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load inquiries" onRetry={refetch} />
        ) : rows.length === 0 ? (
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end. ${data.total} ${data.total === 1 ? 'inquiry' : 'inquiries'}${hasFilters ? ' match' : ' in all'}.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 })}>
                  Go to page 1
                </Button>
              }
            />
          ) : hasFilters ? (
            <EmptyState
              title="No inquiries match"
              description="Try another search or filter."
              action={
                <Button size="sm" onClick={() => update({ q: undefined, status: undefined, topic: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState title="No inquiries yet" description="Questions sent from scorelm.com and league sites appear here." />
          )
        ) : (
          <>
            {loadStatus === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn't refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <DataTable caption="Inquiries" rows={rows} columns={COLUMNS} rowKey={(i) => i.id} rowHref={(i) => `/growth/inquiries/${i.id}`} busy={isFetching && loadStatus === 'success'} />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="inquiries" onPageChange={(p) => update({ page: p })} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
